import type { SSEEvent } from '@/types/ares';

export type SSECallback = (event: SSEEvent) => void;

export async function parseSSEStream(
  response: Response,
  onEvent: SSECallback,
  onError: (error: Error) => void,
  signal?: AbortSignal
): Promise<void> {
  const reader = response.body?.getReader();
  if (!reader) {
    onError(new Error('No response body'));
    return;
  }

  const decoder = new TextDecoder();
  let buffer = '';
  let currentEvent = '';

  try {
    while (true) {
      if (signal?.aborted) break;

      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';

      for (const line of lines) {
        const trimmed = line.trim();

        if (trimmed.startsWith('event:')) {
          currentEvent = trimmed.slice(6).trim();
        } else if (trimmed.startsWith('data:')) {
          const dataStr = trimmed.slice(5).trim();
          if (!dataStr) continue;

          try {
            const data = JSON.parse(dataStr);
            const eventType = currentEvent || inferEventType(data);
            onEvent({
              event: eventType as SSEEvent['event'],
              data,
            });
          } catch {
            // Skip malformed JSON
          }
          currentEvent = '';
        }
      }
    }
  } catch (err) {
    if (signal?.aborted) return;
    onError(err instanceof Error ? err : new Error(String(err)));
  } finally {
    reader.releaseLock();
  }
}

function inferEventType(data: Record<string, unknown>): string {
  if ('stage' in data && 'message' in data) return 'status';
  if ('text' in data && Object.keys(data).length <= 2) return 'reasoning';
  if ('success' in data) return 'result';
  if ('error' in data) return 'error';
  return 'result';
}
